/**
 * Admin API
 */

import apiClient from "./client";
import type {
  DashboardMetrics,
  AuditLogParams,
  AuditLogResponse,
  AnalyticsParams,
  AnalyticsData,
  DiscountAnalysisParams,
  DiscountAnalysisData,
  AttendanceReportParams,
  AttendanceReportData,
  HifdhProgressReportParams,
  HifdhProgressReportData,
  FailedPaymentsReportParams,
  FailedPaymentsReportData,
  AutopayEnrollmentReportParams,
  AutopayEnrollmentReportData,
} from "@/types/admin";

export const adminApi = {
  /**
   * Get dashboard metrics
   */
  getMetrics: (params?: { campus_id?: string }) =>
    apiClient.get<DashboardMetrics>("/api/admin/v1/dashboard", { params }),

  /**
   * Get analytics data for charts and dashboards
   */
  getAnalytics: (params: AnalyticsParams) =>
    apiClient.get<AnalyticsData>("/api/admin/v1/analytics", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.campus_id && { campus_id: params.campus_id }),
      },
    }),

  /**
   * Get audit log with filters
   */
  getAuditLog: (params?: AuditLogParams) =>
    apiClient.get<AuditLogResponse>("/api/admin/v1/audit-log", { params }),

  /**
   * Get discount analysis report data
   */
  getDiscountAnalysis: (params: DiscountAnalysisParams) =>
    apiClient.get<DiscountAnalysisData>("/api/admin/v1/reports/discount-analysis", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.campus_id && { campus_id: params.campus_id }),
      },
    }),

  /**
   * Get attendance report data
   */
  getAttendanceReport: (params: AttendanceReportParams) =>
    apiClient.get<AttendanceReportData>("/api/admin/v1/reports/attendance", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.campus_id && { campus_id: params.campus_id }),
        ...(params.class_id && { class_id: params.class_id }),
      },
    }),

  /**
   * Get Hifdh progress report data
   */
  getHifdhProgressReport: (params: HifdhProgressReportParams) =>
    apiClient.get<HifdhProgressReportData>("/api/admin/v1/reports/hifdh-progress", {
      params: {
        month: params.month,
        ...(params.campus_id && { campus_id: params.campus_id }),
        ...(params.class_id && { class_id: params.class_id }),
      },
    }),

  /**
   * Get failed payments report data
   */
  getFailedPaymentsReport: (params: FailedPaymentsReportParams) =>
    apiClient.get<FailedPaymentsReportData>("/api/admin/v1/reports/failed-payments", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.campus_id && { campus_id: params.campus_id }),
      },
    }),

  /**
   * Get autopay enrollment report data
   */
  getAutopayEnrollmentReport: (params?: AutopayEnrollmentReportParams) =>
    apiClient.get<AutopayEnrollmentReportData>("/api/admin/v1/reports/autopay-enrollment", {
      params: params?.campus_id ? { campus_id: params.campus_id } : undefined,
    }),
};
